<template>
  <div>
    <div class="alert-warning" v-if="getWarningMessage">
      <h4> {{ getWarningMessage }} </h4>
    </div>
    <Header height="50px">
      <div class="brand">
        <svgicon name="pando" width="32" height="32" :original="true"></svgicon>
        <h1 v-if="!titleOnly">{{currentClient && currentClient.name}} - {{title}}</h1>
        <h1 v-else>{{title}}</h1>
      </div>
      <div class="mr-120 d-flex">
        <div v-if="isPackingIndentEnabled">
          <span @click="toggleGlobalSearch">
          <svgicon name="icon-global-search" class="icon-transparent pointer"
                  width="29" height="24" :original="true" />
          </span>
          <div class="search-global" v-if="openGlobalSearch">
            <div class="bg-overlay"></div>
            <div class="search-inner" v-click-outside="toggleGlobalSearch">
              <div class="search-top">
                <span class="input-prepend">
                  <svgicon name="search" width="18" height="20" :original="true" />
                </span>
                <Input placeholder="Search for an Indent ID / Delivery ID"
                      v-on:input="debounceInput"
                      v-model="searchElement"></Input>
                <span class="input-append">
                  <span class="pointer" @click="resetElements">
                    <svgicon name="cancelblack" width="11" height="11" :original="true" />
                  </span>
                </span>
              </div>

              <div class="search-bottom" v-if="searchLoading">
                <div class="centertext">
                  <h3>Loading...</h3>
                </div>
              </div>

              <div class="search-bottom" v-else-if="lastSearched
              && !searchResponse.length && lastSearched.length">
                <h5>
                  Recently Searched
                </h5>
                <ul class="search-list" v-for="(route) in lastSearched" @click="searchByLast(route)">
                  <li>{{ route }}</li>
                </ul>
              </div>

              <div class="search-bottom" v-else>
                  <h6 class="text-capitalize">
                    <small class="ml-auto">
                      {{ searchResponse && `${searchResponse.length} Result(s)` }}
                    </small>
                  </h6>
                  <div v-for="(data, index) in searchResponse" :key="index" :id="index">
                    <Tooltip content="Not authorized" placement="top" :disabled="data && !data.disable">
                      <router-link :to="getSearchRoute(data)" @click.native="filter(data)" >
                        <Row :gutter="5" type="flex" class="grid-list-row pointer">
                          <Col :span="8" align="middle" class="flex-column align-items-baseline">
                            <div>
                              <p class="text-md">
                                {{ data.indentNumber || data.deliveryNumber[0] }}
                              </p>
                            </div>
                            <div>
                              Available at <span class="text-blue-dark1">{{ capitalizeFirstLetter(data.module) }}</span>
                            </div>
                          </Col>
                          <Col :span="10" align="middle">
                        <span class="text-normal">
                          {{ data.carrier || '' }}
                        </span>
                          </Col>
                          <Col :span="6" align="middle" class="col-push text-right">
                        <span :class="`status ${statusColor(data.status) || 'status-purple'}`">
                          {{$t(statusName(data.status))}}
                        </span>
                          </Col>
                        </Row>
                      </router-link>
                    </Tooltip>
                </div>
              </div>

            </div>
          </div>
        </div>
        <div class="ml-15">
          <div class="release-icon" :class="{'is-active': showReleasePopup }" @click="showReleasePopup = true" >
            <svgicon :name="getReleaseIcon" class="pointer"
                  width="24" height="24" :original="true"/>
          </div>
          <div class="release-popover-overlay" v-if="showReleasePopup"></div>
          <div class="popover-custom popover-blue no-border is-action" placement="bottomright"
            v-if="showReleasePopup && getReleaseNotes.length">
              <div class="popover-body with-arrow bg-white mr-m5" style="width:350px; max-height: 305px;">
                <div class="popover-header">
                  <i class="icon-header">
                    <svgicon name="icon-speaker" width="12" height="12" />
                  </i>
                  <span>{{$t('WHATS_NEW')}}</span>
                </div>
                <div class="popover-summary">
                  <p>
                    We, at Pando, strive to add new features and upgrade the existing
                    features in our product to improve your experience
                    <br/><br/>
                    See what's new this month.
                  </p>
                  <ul class="list-points">
                    <li v-for="(_update, index) in getReleaseNotes" :key="`update-${index}`">
                      <div>
                      {{_update.info}}
                      </div>
                      <Link :underline="false" v-if="_update.link" @click="openLink(_update.link)">
                        {{$t('LEARN_MORE')}}
                      </Link>
                    </li>
                  </ul>
                </div>
                <div class="popover-footer">
                  <Button type="text" class="text-white" size="mini" @click="closeReleasePopup">
                    {{$t('SEE_IT_LATER')}}
                  </Button>
                  <Button type="primary" size="mini" @click="openReleaseNotes">
                    {{$t('EXPLORE_NOW')}}
                  </Button>
                </div>
              </div>
          </div>
        </div>
        <div class="ml-15 notification-icon"
          v-if="hasExceptionsView"
        >
          <!-- Add icon below and remove Button -->
          <!-- <svgicon name="icon-release-off" class="pointer"
                  width="16" height="24" :original="true" @click="openReleaseNotes" /> -->
          <svgicon name="icon-notification-active" class="pointer"
                  width="24" height="24" :original="true" @click="goToExceptions" />

        </div>
      </div>
    </Header>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { debounce } from 'lodash';
import indentColors from '../../constants/global-search';

const sourceUrl = 'https://support.pando.in/support/solutions/folders/44001233256';

export default {
  data() {
    return {
      openGlobalSearch: false,
      searchElement: '',
      searchResponse: [],
      indentColors,
      searchedDelivery: '',
      module: '',
      lastSearched: null,
      searchLoading: false,
      showReleasePopup: false,
      releaseNotesUpdates: null,
      popoverClosed: false,
    };
  },
  computed: {
    ...mapGetters({
      currentClient: 'app/currentClient',
      currentDepot: 'order/currentDepot',
      globalSearchQ: 'order/globalSearchQ',
      currentUser: 'app/currentUser',
      releaseNotesSettings: 'app/releaseNotesSettings',
      getPaymentRemainderConfig: 'app/getPaymentRemainderConfig',
    }),
    getWarningMessage() {
      let warningMessage = '';
      const currentUserRole = this.currentUser && this.currentUser.role &&
        this.currentUser.role.type;
      const configuredRoles = this.getPaymentRemainderConfig &&
        this.getPaymentRemainderConfig.shipper_roles;
      if (configuredRoles.includes(currentUserRole) && this.$route.name !== 'Login') {
        warningMessage = this.getPaymentRemainderConfig && this.getPaymentRemainderConfig.warning_message;
      }
      return warningMessage;
    },
    title() {
      const title = this.$route.meta.tlabel ? this.$t(this.$route.meta.tlabel) : this.$route.meta.title;
      return title || '';
    },
    titleOnly() {
      return this.$route.meta.titleOnly || false;
    },
    isPackingIndentEnabled() {
      return this.$hasFeature('packing_indent');
    },
    hasExceptionsView() {
      return (
        this.$route.name !== 'Exception' &&
        (this.$hasPermission('view_exception') ||
        this.$hasPermission('view_integration_log'))
      );
    },
    getReleaseNotes() {
      if (this.releaseNotesUpdates) {
        return (this.releaseNotesUpdates.details && this.releaseNotesUpdates.details.length)
          ? this.releaseNotesUpdates.details : [];
      }
      return [];
    },
    releaseIsVisited() {
      return this.currentUser.release_notes_visited;
    },
    getReleaseIcon() {
      if (!this.releaseIsVisited && !this.popoverClosed) {
        return 'icon-release-on';
      }
      return 'icon-release-off';
    },
  },
  watch: {
    searchInput() {
      this.debounceInput();
    },
  },
  mounted() {
    this.$root.$on('resetGlobalSearch', this.resetSearch);
    this.releaseNotesUpdates = this.releaseNotesSettings ? this.releaseNotesSettings : this.releaseNotesUpdates;
    if (!this.currentUser.release_notes_visited && this.releaseNotesUpdates.release_notification_required) {
      this.showReleasePopup = true;
    }
  },
  methods: {
    openLink(url) {
      window.open(url, '_blank');
      this.closeReleasePopup();
    },
    openReleaseNotes() {
      const url = this.releaseNotesUpdates.source_link && this.releaseNotesUpdates.source_link !== '' ? this.releaseNotesUpdates.source_link : sourceUrl;
      window.open(url, '_blank');
      this.closeReleasePopup();
    },
    async toggleGlobalSearch() {
      this.openGlobalSearch = !this.openGlobalSearch;
      if (this.openGlobalSearch) {
        this.lastSearched = JSON.parse(localStorage.getItem('globalsearch')) || null;
      } else {
        this.resetElements();
      }
    },
    debounceInput: debounce(async function () {
      const lastSearchedArr = [];
      this.searchLoading = true;
      const lastSearchedEle = JSON.parse(localStorage.getItem('globalsearch'));
      if (lastSearchedEle && lastSearchedEle.length) {
        if (lastSearchedEle.length > 2) {
          lastSearchedEle.splice(0, 1);
          lastSearchedArr.push(...lastSearchedEle);
        } else {
          lastSearchedArr.push(...lastSearchedEle);
        }
      }
      const elementExist = lastSearchedEle && lastSearchedEle.length
        && lastSearchedEle.find(el => el === this.searchElement);
      if (!elementExist) {
        lastSearchedArr.push(this.searchElement);
      }
      localStorage.setItem('globalsearch', JSON.stringify(lastSearchedArr));
      const data = await this.$store.dispatch('order/globalSearch', { filterKey: this.searchElement });
      if (data) {
        this.searchResponse = data && data.data;
      }
      this.searchLoading = false;
    }, 1000),
    searchByLast(data) {
      this.searchElement = data;
      this.debounceInput();
    },
    capitalizeFirstLetter(data) {
      return data.charAt(0).toUpperCase() + data.slice(1);
    },
    statusName(id) {
      const mainStatus = this.$statusUtil.mainStatus;
      const mainStatusId = this.$statusUtil.getMainStatusId(id);
      return (mainStatus.find(status => status.id === mainStatusId) &&
        mainStatus.find(status => status.id === mainStatusId).tlabel) || id;
    },
    statusColor(id) {
      const mainStatusId = this.$statusUtil.getMainStatusId(id);
      return indentColors.find(status => status.id === mainStatusId) &&
        indentColors.find(status => status.id === mainStatusId).color;
    },
    packServiceUrl() {
      let url = window.location.host;
      const index = url.indexOf('.');
      url = (index !== -1) ?
        `${url.substring(0, index)}-pack${url.substring(index, url.length)}`
        : url;
      return url;
    },
    getSearchRoute(data) {
      if (data && (data.module === 'pack' || (data.module === 'traffic' && !data.disable))) {
        const search = `delivery=${data.deliveryNumber[0]}&screen=${data.availableAtTraffic ? 'traffic' : 'packing'}`;
        return { name: 'RedirectPage', query: { serviceUrl: this.packServiceUrl(data), packingSearch: search } };
      }
      if (data && data.module === 'manage') {
        return { name: 'OutboundIndentsV2' };
      }
      return { name: '' };
    },
    async filter(data) {
      this.searchedDelivery = data.deliveryNumber[0];
      this.module = data.availableAtTraffic ? 'traffic' : 'pack';
      if (data && data.module === 'manage') {
        const obj = {
          depot_id: this.currentDepot,
          order_number: data.indentNumber,
          page: 1,
          category: 0,
        };
        await this.toggleGlobalSearch();
        this.$root.$emit('openGlobalSearch');
        this.$store.commit('order/GLOBAL_SEARCH_FILTER', true);
        await this.$store.dispatch('order/filter', { params: obj });
      }
    },
    resetElements() {
      this.searchElement = '';
      this.searchResponse = [];
    },
    async resetSearch() {
      this.$root.$emit('closeGlobalSearch');
      this.searchElement = '';
    },
    goToExceptions() {
      this.$router.push({ name: 'Exception' });
    },
    async closeReleasePopup() {
      this.showReleasePopup = false;
      this.popoverClosed = true;
      if (!this.releaseIsVisited) {
        await this.$store.dispatch('app/setReleaseNotesViewed');
      }
    },
  },
};
</script>
